import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Shield, Loader2, CreditCard, MessageCircle, Mail, CloudUpload, Gift, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import type { Json } from "@/integrations/supabase/types";
import { BrevoMappingsManager } from "./BrevoMappingsManager";
import { BrevoSyncStatus } from "./users/BrevoSyncStatus";
import { useBrevoSync } from "@/hooks/useBrevoSync";
import AICopywriterSettings from "./AICopywriterSettings";
import CancellationPolicySettings from "./CancellationPolicySettings";
import BringFriendSettings from "./BringFriendSettings";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
interface SettingValue {
  enabled: boolean;
}

interface WhatsAppSettingValue {
  enabled: boolean;
  link: string;
}

interface AppSettingRow {
  key: string;
  value: Json;
  updated_at: string;
  updated_by: string | null;
}

function SettingToggle({
  settingKey,
  title,
  description,
  enabledMessage,
  disabledMessage,
  warningMessage,
  icon: Icon,
}: {
  settingKey: string;
  title: string;
  description: string;
  enabledMessage: string;
  disabledMessage: string;
  warningMessage?: string;
  icon: React.ElementType;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);

  const { data: setting, isLoading } = useQuery({
    queryKey: ["app-settings", settingKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("*")
        .eq("key", settingKey)
        .single();

      if (error) throw error;
      return data as AppSettingRow;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from("app_settings")
        .update({ 
          value: { enabled } as Json,
          updated_at: new Date().toISOString(),
          updated_by: user?.id 
        })
        .eq("key", settingKey);

      if (error) throw error;
      return enabled;
    },
    onMutate: () => {
      setIsUpdating(true);
    },
    onSuccess: (enabled) => {
      // Set cache with the same structure as queryFn returns (full row object)
      queryClient.setQueryData(["app-settings", settingKey], (old: AppSettingRow | undefined) => ({
        key: settingKey,
        value: { enabled },
        updated_at: new Date().toISOString(),
        updated_by: old?.updated_by ?? null,
      }));
      queryClient.invalidateQueries({ queryKey: ["app-settings", settingKey] });
      toast({
        title: enabled ? enabledMessage : disabledMessage,
        description: `${title} has been ${enabled ? "enabled" : "disabled"}.`,
      });
    },
    onError: (error) => {
      console.error(`Failed to update ${settingKey}:`, error);
      toast({
        title: "Error",
        description: `Failed to update ${title.toLowerCase()}. Please try again.`,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsUpdating(false);
    },
  });

  const settingValue = setting?.value as unknown as SettingValue | undefined;
  const isEnabled = settingValue?.enabled ?? true;

  if (isLoading) {
    return (
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="space-y-0.5">
          <Label className="text-base font-medium">{title}</Label>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="space-y-0.5">
          <Label htmlFor={`${settingKey}-toggle`} className="text-base font-medium">
            {title}
          </Label>
          <p className="text-sm text-muted-foreground">{description}</p>
          {setting?.updated_at && (
            <p className="text-xs text-muted-foreground">
              Last updated: {format(new Date(setting.updated_at), "PPp")}
            </p>
          )}
        </div>
        <Switch
          id={`${settingKey}-toggle`}
          checked={isEnabled}
          onCheckedChange={(checked) => updateMutation.mutate(checked)}
          disabled={isUpdating}
        />
      </div>

      {!isEnabled && warningMessage && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Warning:</strong> {warningMessage}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-2 text-sm">
        <div className={`h-2 w-2 rounded-full ${isEnabled ? "bg-green-500" : "bg-red-500"}`} />
        <span className={isEnabled ? "text-green-600" : "text-red-600"}>
          {title} is currently {isEnabled ? "enabled" : "disabled"}
        </span>
      </div>
    </div>
  );
}

function WhatsAppBubbleSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const [linkValue, setLinkValue] = useState("");

  const { data: setting, isLoading } = useQuery({
    queryKey: ["app-settings", "whatsapp_bubble"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("*")
        .eq("key", "whatsapp_bubble")
        .single();

      if (error) throw error;
      return data as AppSettingRow;
    },
  });

  const settingValue = setting?.value as unknown as WhatsAppSettingValue | undefined;
  const isEnabled = settingValue?.enabled ?? false;
  const currentLink = settingValue?.link ?? "";

  // Sync local state with fetched value
  useState(() => {
    if (currentLink && linkValue === "") {
      setLinkValue(currentLink);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ enabled, link }: { enabled: boolean; link: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from("app_settings")
        .update({ 
          value: { enabled, link } as unknown as Json,
          updated_at: new Date().toISOString(),
          updated_by: user?.id 
        })
        .eq("key", "whatsapp_bubble");

      if (error) throw error;
      return { enabled, link };
    },
    onMutate: () => {
      setIsUpdating(true);
    },
    onSuccess: ({ enabled }) => {
      queryClient.invalidateQueries({ queryKey: ["app-settings", "whatsapp_bubble"] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-bubble"] });
      toast({
        title: enabled ? "WhatsApp Bubble Enabled" : "WhatsApp Bubble Disabled",
        description: "WhatsApp bubble settings have been updated.",
      });
    },
    onError: (error) => {
      console.error("Failed to update whatsapp_bubble:", error);
      toast({
        title: "Error",
        description: "Failed to update WhatsApp bubble settings. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsUpdating(false);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="space-y-0.5">
          <Label className="text-base font-medium">WhatsApp Contact Bubble</Label>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="space-y-0.5">
          <Label htmlFor="whatsapp-toggle" className="text-base font-medium">
            WhatsApp Contact Bubble
          </Label>
          <p className="text-sm text-muted-foreground">
            Show a floating WhatsApp button on all pages for users to contact you
          </p>
          {setting?.updated_at && (
            <p className="text-xs text-muted-foreground">
              Last updated: {format(new Date(setting.updated_at), "PPp")}
            </p>
          )}
        </div>
        <Switch
          id="whatsapp-toggle"
          checked={isEnabled}
          onCheckedChange={(checked) => 
            updateMutation.mutate({ enabled: checked, link: linkValue || currentLink })
          }
          disabled={isUpdating}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="whatsapp-link">WhatsApp Link</Label>
        <div className="flex gap-2">
          <Input
            id="whatsapp-link"
            placeholder="https://wa.me/1234567890 or https://chat.whatsapp.com/..."
            defaultValue={currentLink}
            onChange={(e) => setLinkValue(e.target.value)}
            className="flex-1"
          />
          <button
            onClick={() => updateMutation.mutate({ enabled: isEnabled, link: linkValue })}
            disabled={isUpdating || linkValue === currentLink}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
          >
            {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Use a WhatsApp direct link (wa.me) or group invite link (chat.whatsapp.com)
        </p>
      </div>

      <div className="flex items-center gap-2 text-sm">
        <div className={`h-2 w-2 rounded-full ${isEnabled ? "bg-green-500" : "bg-red-500"}`} />
        <span className={isEnabled ? "text-green-600" : "text-red-600"}>
          WhatsApp bubble is currently {isEnabled ? "enabled" : "disabled"}
        </span>
      </div>
    </div>
  );
}

function BrevoFolderNameSetting() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const [folderName, setFolderName] = useState("");

  const { data: setting, isLoading } = useQuery({
    queryKey: ["app-settings", "brevo_folder_name"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("*")
        .eq("key", "brevo_folder_name")
        .single();

      if (error) throw error;
      return data as AppSettingRow;
    },
  });

  const settingValue = setting?.value as { name?: string } | undefined;
  const currentName = settingValue?.name ?? "nimi_app";

  const updateMutation = useMutation({
    mutationFn: async (name: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from("app_settings")
        .update({ 
          value: { name } as unknown as Json,
          updated_at: new Date().toISOString(),
          updated_by: user?.id 
        })
        .eq("key", "brevo_folder_name");

      if (error) throw error;
      return name;
    },
    onMutate: () => {
      setIsUpdating(true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["app-settings", "brevo_folder_name"] });
      toast({
        title: "Folder Name Updated",
        description: "Brevo folder name has been updated. New contacts will use this folder.",
      });
    },
    onError: (error) => {
      console.error("Failed to update brevo_folder_name:", error);
      toast({
        title: "Error",
        description: "Failed to update Brevo folder name. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsUpdating(false);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="space-y-0.5">
          <Label className="text-base font-medium">Brevo Folder Name</Label>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="brevo-folder">Brevo Folder Name</Label>
      <div className="flex gap-2">
        <Input
          id="brevo-folder"
          placeholder="nimi_app"
          defaultValue={currentName}
          onChange={(e) => setFolderName(e.target.value)}
          className="flex-1"
        />
        <button
          onClick={() => updateMutation.mutate(folderName || currentName)}
          disabled={isUpdating || (folderName === "" || folderName === currentName)}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
        >
          {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        Folder in Brevo where tag lists will be created. Changing this won't move existing lists.
      </p>
    </div>
  );
}

function BrevoMasterListSetting() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const [listName, setListName] = useState("");

  const { data: setting, isLoading } = useQuery({
    queryKey: ["app-settings", "brevo_master_list_name"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("*")
        .eq("key", "brevo_master_list_name")
        .maybeSingle();

      if (error) throw error;
      return data as AppSettingRow | null;
    },
  });

  const settingValue = setting?.value as { name?: string } | undefined;
  const currentName = settingValue?.name ?? "_All Synced Contacts";

  const updateMutation = useMutation({
    mutationFn: async (name: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from("app_settings")
        .upsert({ 
          key: "brevo_master_list_name",
          value: { name } as unknown as Json,
          updated_at: new Date().toISOString(),
          updated_by: user?.id 
        });

      if (error) throw error;
      return name;
    },
    onMutate: () => {
      setIsUpdating(true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["app-settings", "brevo_master_list_name"] });
      toast({
        title: "Master List Name Updated",
        description: "Brevo master list name has been updated.",
      });
    },
    onError: (error) => {
      console.error("Failed to update brevo_master_list_name:", error);
      toast({
        title: "Error",
        description: "Failed to update Brevo master list name. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsUpdating(false);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="space-y-0.5">
          <Label className="text-base font-medium">Master Sync List Name</Label>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="brevo-master-list">Master Sync List Name</Label>
      <div className="flex gap-2">
        <Input
          id="brevo-master-list"
          placeholder="_All Synced Contacts"
          defaultValue={currentName}
          onChange={(e) => setListName(e.target.value)}
          className="flex-1"
        />
        <button
          onClick={() => updateMutation.mutate(listName || currentName)}
          disabled={isUpdating || (listName === "" || listName === currentName)}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
        >
          {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        List in Brevo where all synced contacts are added. Required for batch imports.
      </p>
    </div>
  );
}

function EarlyBirdSettingsCard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState<string>("");
  const [pct, setPct] = useState<string>("");

  const { data: rows, isLoading } = useQuery({
    queryKey: ["app-settings", "early_bird_all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("*")
        .in("key", ["early_bird_enabled", "early_bird_days_before", "early_bird_discount_percent"]);
      if (error) throw error;
      return data as AppSettingRow[];
    },
  });

  const get = (k: string) => rows?.find((r) => r.key === k);
  const enabled = (get("early_bird_enabled")?.value as unknown as boolean) ?? false;
  const currentDays = (get("early_bird_days_before")?.value as unknown as number) ?? 14;
  const currentPct = (get("early_bird_discount_percent")?.value as unknown as number) ?? 10;

  const upsertMutation = useMutation({
    mutationFn: async (patch: { key: string; value: Json }[]) => {
      const { data: { user } } = await supabase.auth.getUser();
      for (const p of patch) {
        const { error } = await supabase
          .from("app_settings")
          .update({ value: p.value, updated_at: new Date().toISOString(), updated_by: user?.id })
          .eq("key", p.key);
        if (error) throw error;
      }
    },
    onMutate: () => setSaving(true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["app-settings", "early_bird_all"] });
      toast({ title: "Early bird settings updated" });
    },
    onError: (e: any) => toast({ title: "Error", description: e?.message || "Failed to update", variant: "destructive" }),
    onSettled: () => setSaving(false),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          Early Bird Pricing (Global)
        </CardTitle>
        <CardDescription>
          Default discount applied to paid events booked N+ days before start. Series and individual events can override.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label className="text-base font-medium">Enable early bird by default</Label>
            <p className="text-sm text-muted-foreground">All paid events use these defaults unless overridden.</p>
          </div>
          <Switch
            checked={enabled}
            disabled={isLoading || saving}
            onCheckedChange={(checked) =>
              upsertMutation.mutate([{ key: "early_bird_enabled", value: checked as unknown as Json }])
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="eb-days">Days before event</Label>
            <Input
              id="eb-days"
              type="number"
              min={1}
              defaultValue={currentDays}
              onChange={(e) => setDays(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="eb-pct">Discount %</Label>
            <Input
              id="eb-pct"
              type="number"
              min={0}
              max={100}
              step="0.1"
              defaultValue={currentPct}
              onChange={(e) => setPct(e.target.value)}
            />
          </div>
        </div>
        <Button
          disabled={saving || isLoading}
          onClick={() => {
            const patch: { key: string; value: Json }[] = [];
            if (days !== "" && Number(days) !== currentDays) {
              patch.push({ key: "early_bird_days_before", value: Number(days) as unknown as Json });
            }
            if (pct !== "" && Number(pct) !== currentPct) {
              patch.push({ key: "early_bird_discount_percent", value: Number(pct) as unknown as Json });
            }
            if (patch.length) upsertMutation.mutate(patch);
          }}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}

export function AdminSettings() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Brevo Integration
          </CardTitle>
          <CardDescription>
            Configure how user data syncs to Brevo
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <BrevoSyncCard />
          <BrevoFolderNameSetting />
          <BrevoMasterListSetting />
          <BrevoMappingsManager />
        </CardContent>
      </Card>

      <EarlyBirdSettingsCard />

      <CancellationPolicySettings />

      <BringFriendSettings />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Feature Settings
          </CardTitle>
          <CardDescription>
            Control visibility of features across the application
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingToggle
            settingKey="credits_purchase_enabled"
            title="Buy Credits Section"
            description="Show the credit pack purchase section on user profiles"
            enabledMessage="Buy Credits Enabled"
            disabledMessage="Buy Credits Disabled"
            icon={CreditCard}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Wrapped Settings
          </CardTitle>
          <CardDescription>
            Control the Year in Art Wrapped feature visibility
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingToggle
            settingKey="wrapped_enabled"
            title="Wrapped Feature"
            description="Show the Wrapped menu button and admin tab"
            enabledMessage="Wrapped Enabled"
            disabledMessage="Wrapped Disabled"
            icon={Gift}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security Settings
          </CardTitle>
          <CardDescription>
            Manage security features for the application
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingToggle
            settingKey="captcha_enabled"
            title="CAPTCHA Protection"
            description="Require CAPTCHA verification on sign-up and sign-in forms"
            enabledMessage="CAPTCHA Enabled"
            disabledMessage="CAPTCHA Disabled"
            warningMessage="CAPTCHA protection is currently disabled. This leaves sign-up and sign-in forms vulnerable to bot attacks and spam registrations. Only disable this temporarily when the CAPTCHA provider is experiencing issues."
            icon={Shield}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            WhatsApp Settings
          </CardTitle>
          <CardDescription>
            Configure the WhatsApp contact bubble
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <WhatsAppBubbleSettings />
        </CardContent>
      </Card>
    </div>
  );
}

function BrevoSyncCard() {
  const { 
    syncAllUsers, 
    isSyncing, 
    progress, 
    activeJob, 
    recentJobs, 
    listRecentJobs 
  } = useBrevoSync();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-base font-medium">Sync Users to Brevo</Label>
          <p className="text-sm text-muted-foreground">
            Sync all user profiles and tag memberships to Brevo
          </p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" disabled={isSyncing}>
              {isSyncing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {progress.status || "Syncing..."}
                </>
              ) : (
                <>
                  <CloudUpload className="h-4 w-4 mr-2" />
                  Sync All Users
                </>
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Sync All Users to Brevo</AlertDialogTitle>
              <AlertDialogDescription>
                This will sync all users to Brevo, including their profile data and tag list memberships.
                The sync runs in the background - you can close this page and check back later.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={syncAllUsers}>
                Start Sync
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      
      <BrevoSyncStatus 
        activeJob={activeJob} 
        recentJobs={recentJobs} 
      />
    </div>
  );
}
